import { useQuery } from '@tanstack/react-query';
import { KeyRound, Terminal, UserRound } from 'lucide-react';
import type { ReactNode } from 'react';

import { WHOAMI_KEY } from '@/components/auth/RequireAuth';
import { Page, PageHeader } from '@/components/layout/PageHeader';
import { ChangePasswordCard } from '@/components/settings/ChangePasswordCard';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormAlert } from '@/components/ui/form-alert';
import { Skeleton } from '@/components/ui/skeleton';
import { genericErrorMessage } from '@/lib/api-errors';
import { authApi } from '@/lib/auth-api';
import { formatDateTime } from '@/lib/format';
import { adminDisplayName } from '@/lib/use-auth';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';

export const BACKUP_CODES_TOTAL = 8;
/** Seuil d'alerte : à 2 codes ou moins, prévoir un reset MFA (qui régénère les 8 codes). */
export const BACKUP_CODES_LOW = 2;

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex flex-col gap-1 border-b py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between">
    <dt className="text-sm text-muted-foreground">{label}</dt>
    <dd className="text-sm font-medium">{children}</dd>
  </div>
);

const ADMIN_STATUS_LABELS: Record<string, string> = { ACTIVE: 'Actif', INVITED: 'Invité', SUSPENDED: 'Suspendu' };

export const SettingsPage = () => {
  const accessToken = useAuthStore((s) => s.accessToken);
  const storedAdmin = useAuthStore((s) => s.admin);
  // Même clé que la garde : profil déjà en cache, relu au plus toutes les 60 s
  const whoami = useQuery({ queryKey: [...WHOAMI_KEY, accessToken], queryFn: authApi.whoami, staleTime: 60_000 });
  const backup = useQuery({ queryKey: ['platform', 'backup-codes-count', accessToken], queryFn: authApi.backupCodesCount });
  const admin = whoami.data ?? storedAdmin;
  if (!admin) return null;

  const count = backup.data;
  const low = count !== undefined && count <= BACKUP_CODES_LOW;

  return (
    <Page className="max-w-3xl">
      <PageHeader title="Paramètres" description="Votre compte administrateur plateforme." />

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <UserRound className="h-4 w-4 text-brand-violet" aria-hidden />
            Compte
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl>
            <Row label="Email">{admin.email}</Row>
            <Row label="Nom complet">{adminDisplayName(admin) === admin.email ? '—' : adminDisplayName(admin)}</Row>
            <Row label="Statut">
              <Badge variant={admin.status === 'ACTIVE' ? 'success-soft' : 'muted-soft'} data-testid="admin-status">
                {ADMIN_STATUS_LABELS[admin.status ?? ''] ?? admin.status ?? '—'}
              </Badge>
            </Row>
            <Row label="Dernière connexion">
              <span data-testid="admin-last-login">{formatDateTime(admin.lastLoginAt)}</span>
            </Row>
          </dl>
        </CardContent>
      </Card>

      <Card className={cn(low && 'border-warning/50')}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <KeyRound className="h-4 w-4 text-brand-violet" aria-hidden />
            Double authentification
          </CardTitle>
          <CardDescription>Chaque code de secours ne fonctionne qu'une fois.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <dl>
            <Row label="Codes de secours restants">
              {backup.isPending && <Skeleton className="h-5 w-12" />}
              {backup.isError && <span className="text-danger">indisponible</span>}
              {count !== undefined && (
                <span data-testid="backup-codes-count" className={cn('tabular-nums', low && 'text-amber-700')}>
                  {count} / {BACKUP_CODES_TOTAL}
                </span>
              )}
            </Row>
          </dl>
          {backup.isError && <FormAlert>Nombre de codes indisponible : {genericErrorMessage(backup.error).toLowerCase()}</FormAlert>}
          {low && (
            <FormAlert>
              {count === 0 ? 'Plus aucun code de secours.' : `Plus que ${count} code${count > 1 ? 's' : ''} de secours.`} Demandez un reset MFA
              pour en régénérer 8.
            </FormAlert>
          )}
        </CardContent>
      </Card>

      <ChangePasswordCard />

      <Card className="bg-muted/40">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Terminal className="h-4 w-4 text-brand-violet" aria-hidden />
            Opérations réservées à l'équipe technique
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground" data-testid="cli-note">
          <p>
            Un autre admin peut réinitialiser votre MFA depuis la page Admins. Sans admin disponible, ces opérations passent par
            l'équipe technique, avec vérification d'identité.
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Reset MFA (nouveau secret TOTP + 8 codes de secours) :{' '}
              <code className="rounded bg-white px-1.5 py-0.5 font-mono text-xs text-foreground">
                pnpm platform:reset-mfa --email {admin.email}
              </code>
            </li>
            <li>
              Mot de passe oublié (reset sans l'actuel) :{' '}
              <code className="rounded bg-white px-1.5 py-0.5 font-mono text-xs text-foreground">
                pnpm platform:change-password --email {admin.email}
              </code>
            </li>
          </ul>
        </CardContent>
      </Card>
    </Page>
  );
};
